import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Text,
  Keyboard,
  Platform,
} from 'react-native';
import { Portal, Modal, Searchbar, Card, Chip, IconButton, Divider } from 'react-native-paper';
import type { HomeReview, ReviewItem } from '../types';
import { reviewService } from '../services/reviewService';
import { colors, theme } from '../constants/theme';
import {
  hasHydratedReviewSearchInventory,
  ReviewSearchResult,
  searchReviewItemsLocally,
} from '../utils/reviewItemSearch';

interface ReviewSearchModalProps {
  visible: boolean;
  onDismiss: () => void;
  reviewId: number; // Required - search is always scoped to this review
  onItemSelect?: (item: ReviewSearchResult) => void;
  review?: HomeReview | null;
  additionalItems?: ReviewItem[];
}

export const ReviewSearchModal: React.FC<ReviewSearchModalProps> = ({
  visible,
  onDismiss,
  reviewId,
  onItemSelect: customOnItemSelect,
  review,
  additionalItems = [],
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ReviewSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchVersionRef = useRef(0);
  const hasHydratedInventory = useMemo(
    () => hasHydratedReviewSearchInventory(review),
    [review]
  );

  const cancelPendingSearch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    searchVersionRef.current += 1;
  }, []);

  const searchItems = useCallback(async (query: string, searchVersion: number) => {
    if (!query.trim()) {
      if (searchVersion === searchVersionRef.current) {
        setSearchResults([]);
        setLoading(false);
      }
      return;
    }

    if (hasHydratedInventory) {
      if (searchVersion === searchVersionRef.current) {
        setSearchResults(searchReviewItemsLocally(review, query, additionalItems));
        setLoading(false);
      }
      return;
    }

    try {
      const results = await reviewService.searchReviewItems(query, reviewId);
      if (searchVersion === searchVersionRef.current) {
        setSearchResults(results);
      }
    } catch (error) {
      console.error('Search failed:', error);
      if (searchVersion === searchVersionRef.current) {
        setSearchResults([]);
      }
    } finally {
      if (searchVersion === searchVersionRef.current) {
        setLoading(false);
      }
    }
  }, [additionalItems, hasHydratedInventory, review, reviewId]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);

    cancelPendingSearch();
    setSearchResults([]);

    if (!query.trim()) {
      setLoading(false);
      return;
    }

    const searchVersion = searchVersionRef.current;
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void searchItems(query, searchVersion);
    }, 300);
  }, [cancelPendingSearch, searchItems]);

  const handleItemPress = (item: ReviewSearchResult) => {
    // Add to recent searches
    if (searchQuery.trim() && !recentSearches.includes(searchQuery.trim())) {
      const newRecent = [searchQuery.trim(), ...recentSearches.slice(0, 4)];
      setRecentSearches(newRecent);
    }

    // Navigation belongs to the caller so the correct Reviews/Results stack is
    // retained. Header search always supplies this through SearchProvider.
    if (customOnItemSelect) {
      customOnItemSelect(item);
    } else {
      console.error('[ReviewSearchModal] Missing item selection handler', {
        reviewId,
        itemId: item.id,
      });
    }

    onDismiss();
    clearSearch();
  };

  const clearSearch = () => {
    cancelPendingSearch();
    setSearchQuery('');
    setSearchResults([]);
    setLoading(false);
  };

  const handleRecentSearchPress = (query: string) => {
    handleSearchChange(query);
  };

  const handleModalDismiss = () => {
    clearSearch();
    onDismiss();
  };

  // Handle keyboard events
  useEffect(() => {
    const keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', () => {
      // Keyboard dismissed
    });

    // Keyboard event handling
    const handleKeyDown = (event: KeyboardEvent) => {
      // Escape key to close modal
      if (event.key === 'Escape' && visible) {
        handleModalDismiss();
      }
    };

    // Add keyboard event listener only on web
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      keyboardDidHideListener.remove();
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [visible]);

  useEffect(() => () => cancelPendingSearch(), [cancelPendingSearch]);

  const renderSearchResult = ({ item }: { item: ReviewSearchResult }) => (
    <TouchableOpacity
      testID={`review-search-modal-result-${item.id}`}
      style={styles.resultItem}
      onPress={() => handleItemPress(item)}
    >
      <View style={styles.resultContent}>
        <Text style={styles.itemName}>{item.name}</Text>
        {item.description && (
          <Text style={styles.itemDescription} numberOfLines={2}>
            {item.description}
          </Text>
        )}
        <View style={styles.resultMeta}>
          {item.categoryName && (
            <Chip
              mode="outlined"
              compact
              style={styles.categoryChip}
              textStyle={styles.categoryChipText}
            >
              {item.categoryName}
            </Chip>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderRecentSearch = ({ item }: { item: string }) => (
    <TouchableOpacity
      style={styles.recentItem}
      onPress={() => handleRecentSearchPress(item)}
    >
      <Text style={styles.recentText}>{item}</Text>
    </TouchableOpacity>
  );

  return (
    <Portal>
      <Modal
        testID="review-search-modal"
        visible={visible}
        onDismiss={handleModalDismiss}
        contentContainerStyle={styles.modalContent}
      >
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>
            Search Review Items
          </Text>
          <IconButton
            testID="review-search-modal-close-button"
            icon="close"
            size={24}
            onPress={handleModalDismiss}
            style={styles.closeButton}
          />
        </View>

        <Searchbar
          testID="review-search-modal-input"
          placeholder="Search for review items..."
          onChangeText={handleSearchChange}
          value={searchQuery}
          style={styles.searchbar}
          inputStyle={styles.searchInput}
          iconColor={theme.colors.primary}
          onClearIconPress={clearSearch}
          loading={loading}
          autoFocus={Platform.OS !== 'ios'}
        />

        <View style={styles.contentArea}>
          {searchQuery.trim() === '' && recentSearches.length > 0 && (
            <View style={styles.recentSection}>
              <Text style={styles.sectionTitle}>Recent Searches</Text>
              <FlatList
                data={recentSearches}
                renderItem={renderRecentSearch}
                keyExtractor={(item, index) => `recent-${index}`}
                style={styles.recentList}
                showsVerticalScrollIndicator={false}
                removeClippedSubviews={false}
                initialNumToRender={10}
                maxToRenderPerBatch={10}
              />
              <Divider style={styles.divider} />
            </View>
          )}

          {searchQuery.trim() !== '' && (
            <View style={styles.resultsSection}>
              {searchResults.length > 0 ? (
                <>
                  <View style={styles.resultsHeader}>
                    <Text style={styles.resultsCount}>
                      {searchResults.length} item{searchResults.length !== 1 ? 's' : ''} found
                    </Text>
                  </View>
                  <FlatList
                    data={searchResults}
                    renderItem={renderSearchResult}
                    keyExtractor={(item) => item.id.toString()}
                    style={styles.resultsList}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                    removeClippedSubviews={false}
                    initialNumToRender={10}
                    maxToRenderPerBatch={10}
                    windowSize={10}
                  />
                </>
              ) : !loading && (
                <View style={styles.noResults}>
                  <Text style={styles.noResultsText}>
                    No items found for "{searchQuery}"
                  </Text>
                  <Text style={styles.noResultsSubtext}>
                    Try different keywords or check your spelling
                  </Text>
                </View>
              )}
            </View>
          )}

          {searchQuery.trim() === '' && recentSearches.length === 0 && (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateTitle}>Search Review Items</Text>
              <Text style={styles.emptyStateText}>
                Find an item in this review by typing its name or description
              </Text>
            </View>
          )}
        </View>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modalContent: {
    backgroundColor: 'white',
    margin: 20,
    borderRadius: 12,
    height: '80%',
    maxHeight: '90%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 10,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
    flex: 1,
  },
  closeButton: {
    margin: 0,
  },
  searchbar: {
    marginHorizontal: 20,
    marginBottom: 16,
    backgroundColor: theme.colors.surface,
    elevation: 0,
    boxShadow: 'none',
  },
  searchInput: {
    fontSize: 16,
  },
  contentArea: {
    flex: 1,
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  recentSection: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 12,
  },
  recentList: {
    maxHeight: 120,
  },
  recentItem: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 4,
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
  },
  recentText: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  divider: {
    marginTop: 12,
  },
  resultsSection: {
    flex: 1,
  },
  resultsHeader: {
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    marginBottom: 8,
  },
  resultsCount: {
    fontSize: 14,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  resultsList: {
    flex: 1,
  },
  resultItem: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  resultContent: {
    flex: 1,
  },
  itemName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 4,
  },
  itemDescription: {
    fontSize: 14,
    color: colors.textSecondary,
    marginBottom: 8,
    lineHeight: 20,
  },
  resultMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  categoryChip: {
    backgroundColor: '#e6f8fb',
    height: 24,
  },
  categoryChipText: {
    fontSize: 12,
    color: theme.colors.primary,
  },
  requiredChip: {
    backgroundColor: '#ffebee',
    height: 24,
  },
  requiredChipText: {
    fontSize: 12,
    color: '#d32f2f',
  },
  noResults: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  noResultsText: {
    fontSize: 16,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: 8,
  },
  noResultsSubtext: {
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text,
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 8,
  },
  emptyStateSubtext: {
    fontSize: 12,
    color: colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
  },
});
